import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import MapView from "../components/MapView";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MultiImageUpload from "../components/MultiImageUpload";
import UKLocationAutocomplete from "../components/UKLocationAutocomplete";
import { api, getUser, isAdminUser } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Recycle } from "lucide-react";

const CATEGORIES = ["Furniture", "Electronics", "Toys", "Clothes", "Garden", "Books", "Other"];
const CONDITIONS = [
  { id: "new", label: "New" },
  { id: "like_new", label: "Like new" },
  { id: "used", label: "Used — good" },
  { id: "well_used", label: "Well used" },
];

function derivePostcodeArea(location) {
  // UKLocationAutocomplete may return `{postcode, label, address, lat, lng}`
  // or just an address string. Grab the leading token (e.g. "SW1A 1AA" → "SW1A").
  const raw = location?.postcode || location?.label || location?.address || "";
  const token = String(raw).trim().split(/\s+/)[0].toUpperCase();
  return /^[A-Z]{1,2}\d{1,2}[A-Z]?$/.test(token) ? token : "";
}

export default function ReuseNew() {
  const navigate = useNavigate();
  const user = getUser();
  const [form, setForm] = useState({
    title: "",
    category: "Furniture",
    description: "",
    condition: "used",
    image_urls: [],
    pickup_window: "",
    preferred_date: "",
    location: null,
  });
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    navigate("/login");
    return null;
  }

  const loc = form.location;
  const postcodeArea = derivePostcodeArea(loc);
  const canSubmit =
    form.title.length >= 3 &&
    form.description.length >= 10 &&
    form.image_urls.length >= 1 &&
    form.pickup_window.length >= 3 &&
    !!loc &&
    !!postcodeArea;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (!postcodeArea) {
        toast.error("Pick a UK postcode (e.g. M1 1AE) so requesters know roughly where to travel.");
      } else if (form.image_urls.length === 0) {
        toast.error("Add at least one photo — helps neighbours decide quickly.");
      }
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
        condition: form.condition,
        image_urls: form.image_urls,
        postcode_area: postcodeArea,
        lat: loc.lat,
        lng: loc.lng,
        pickup_window: form.pickup_window.trim(),
        preferred_date: form.preferred_date || null,
      };
      const { data } = await api.post("/reuse-listings", payload, { params: { user_id: user.id } });
      toast.success("Listed for free — neighbours can now request it!");
      navigate(`/reuse/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to list");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="inline-flex items-center gap-2 bg-[#EBE8E0] text-[#1B3624] px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide uppercase">
          <Recycle className="w-3.5 h-3.5" /> Reuse — free give-away
        </div>
        <h1
          data-testid="reuse-new-title"
          className="mt-4 text-3xl sm:text-4xl tracking-tighter font-medium"
          style={{ fontFamily: "Outfit" }}
        >
          List something for reuse
        </h1>
        <p className="mt-2 text-sm text-[#6B6965] max-w-xl">
          Add a photo, describe the item, pick your postcode area. We only publish an approximate
          location (±150 m) — your exact address stays private until you accept a request.
        </p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <Card className="p-7 rounded-2xl border-[#E2DFD8] bg-white">
            <form onSubmit={submit} className="space-y-4">
              <Field label="Title">
                <Input
                  data-testid="reuse-new-titlefield"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={80}
                  placeholder="e.g. Two-seater sofa — free"
                  className="rounded-xl"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="reuse-new-category" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Condition">
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                    <SelectTrigger data-testid="reuse-new-condition" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Description">
                <Textarea
                  data-testid="reuse-new-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={1000}
                  placeholder="Dimensions, quirks, why it's still usable…"
                  className="rounded-xl min-h-[100px]"
                />
              </Field>
              <Field label="UK postcode (city, town or postcode)">
                <UKLocationAutocomplete
                  value={form.location}
                  onChange={(v) => setForm({ ...form, location: v })}
                  placeholder="e.g. M1 1AE, Manchester or SW1A"
                  testId="reuse-new-postcode"
                />
                {loc && (
                  <p className="mt-1 text-xs text-[#6B6965]">
                    Publishing as postcode area <span className="font-medium text-[#1B3624]">{postcodeArea || "—"}</span> · approximate pin only.
                  </p>
                )}
              </Field>
              <Field label="Pickup window">
                <Input
                  data-testid="reuse-new-window"
                  value={form.pickup_window}
                  onChange={(e) => setForm({ ...form, pickup_window: e.target.value })}
                  maxLength={120}
                  placeholder="e.g. Weekday evenings after 6pm, or Sat morning"
                  className="rounded-xl"
                />
              </Field>
              <Field label="Preferred date (optional)">
                <Input
                  data-testid="reuse-new-date"
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  className="rounded-xl"
                />
              </Field>
              <Field label="Photos (at least one required)">
                <MultiImageUpload
                  value={form.image_urls}
                  onChange={(urls) => setForm({ ...form, image_urls: urls })}
                  max={6}
                  testId="reuse-new-image"
                />
                <p className="mt-1 text-xs text-[#6B6965]">
                  Add up to 6 photos — a clear shot on a neutral background gets the fastest responses.
                </p>
              </Field>
              <Button
                data-testid="reuse-new-submit"
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white py-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Listing…" : "List it — free"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </form>
          </Card>

          <div>
            {isAdminUser(user) ? (
              <>
                <div className="text-sm text-[#6B6965] mb-2">Approximate pin preview</div>
                <MapView
                  markers={loc ? [{ id: "preview", lat: loc.lat, lng: loc.lng, title: form.title || "Your item", subtitle: postcodeArea || "postcode area" }] : []}
                  center={loc ? [loc.lat, loc.lng] : [54.5, -2.5]}
                  zoom={loc ? 12 : 5}
                  height="h-[420px]"
                  testId="reuse-new-map"
                />
              </>
            ) : (
              <Card className="rounded-2xl border-[#E2DFD8] bg-[#F6F4F0] p-6 h-full min-h-[420px] flex flex-col justify-center">
                <div className="text-[10px] uppercase tracking-widest text-[#4A6B56] font-semibold">Privacy summary</div>
                <h3 className="mt-2 text-xl font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>Your address stays private.</h3>
                <p className="mt-2 text-sm text-[#6B6965]">
                  We only publish your postcode area <span className="font-medium text-[#1B3624]">({postcodeArea || "e.g. SW1A"})</span>.
                  Even your rough pin is jittered by ±150 m so neighbours know the district but never the doorstep.
                </p>
                <p className="mt-3 text-sm text-[#6B6965]">
                  Once you accept a request, you can share the full address in-app to arrange pickup.
                </p>
              </Card>
            )}
            <p className="mt-2 text-[11px] text-[#9B9893]">
              The published pin is jittered ±150 m for privacy. Nobody sees your exact address unless you accept their request.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
